export const AudienceRelationshipType = {
  FOLLOWER: 'follower',
  CIRCLE_MEMBER: 'circle_member',
  COURSE_ENROLLMENT: 'course_enrollment',
  MEMBERSHIP_SUBSCRIBER: 'membership_subscriber',
  SERVICE_CUSTOMER: 'service_customer',
  BOOKING: 'booking',
  PRIORITY_DM: 'priority_dm',
  EVENT_ATTENDEE: 'event_attendee',
  NEWSLETTER_SUBSCRIBER: 'newsletter_subscriber',
} as const;

export type AudienceRelationshipTypeValue =
  (typeof AudienceRelationshipType)[keyof typeof AudienceRelationshipType];

export const AudienceSourceModule = {
  SOCIAL: 'social',
  COMMUNITIES: 'communities',
  COURSES: 'courses',
  MEMBERSHIPS: 'memberships',
  SERVICES: 'services',
  EVENTS: 'events',
  CRM: 'crm',
  CREATOR_PRESENCE: 'creator_presence',
  MARKETING: 'marketing',
} as const;

export type AudienceSourceModuleValue =
  (typeof AudienceSourceModule)[keyof typeof AudienceSourceModule];

export const AudienceActivityEventType = {
  CIRCLE_JOINED: 'circle.joined',
  CIRCLE_LEFT: 'circle.left',
  CIRCLE_TIER_CHANGED: 'circle.tier_changed',
  CIRCLE_RENEWED: 'circle.renewed',
  COURSE_ENROLLED: 'course.enrolled',
  COURSE_PURCHASED: 'course.purchased',
  COURSE_COMPLETED: 'course.completed',
  COURSE_REFUNDED: 'course.refunded',
  MEMBERSHIP_SUBSCRIBED: 'membership.subscribed',
  MEMBERSHIP_CANCELLED: 'membership.cancelled',
  MEMBERSHIP_RENEWED: 'membership.renewed',
  MEMBERSHIP_PAST_DUE: 'membership.past_due',
  SERVICE_PURCHASED: 'service.purchased',
  SERVICE_REFUNDED: 'service.refunded',
  BOOKING_CREATED: 'booking.created',
  BOOKING_COMPLETED: 'booking.completed',
  BOOKING_NO_SHOW: 'booking.no_show',
  PRIORITY_DM_SUBMITTED: 'priority_dm.submitted',
  PRIORITY_DM_FULFILLED: 'priority_dm.fulfilled',
  DIGITAL_DELIVERED: 'digital.delivered',
  EVENT_RSVP_GOING: 'event.rsvp_going',
  EVENT_RSVP_CANCELLED: 'event.rsvp_cancelled',
  TAG_ADDED: 'tag.added',
  TAG_REMOVED: 'tag.removed',
  SITE_NEWSLETTER_SUBSCRIBED: 'site.newsletter_subscribed',
  SITE_INQUIRY_SUBMITTED: 'site.inquiry_submitted',
} as const;

export type AudienceActivityEventTypeValue =
  (typeof AudienceActivityEventType)[keyof typeof AudienceActivityEventType];

export type AudienceRelationshipPatch = {
  type: AudienceRelationshipTypeValue;
  sourceModule: AudienceSourceModuleValue;
  sourceId: string;
  status?: string | null;
  metadata?: Record<string, unknown>;
};

export type EmitAudienceFactsInput = {
  creatorId: string;
  userId?: string | null;
  email?: string | null;
  eventType: AudienceActivityEventTypeValue;
  sourceModule: AudienceSourceModuleValue;
  relationshipPatch?: AudienceRelationshipPatch;
  activityPayload?: Record<string, unknown>;
  occurredAt?: Date;
  ltvDeltaCents?: number;
};

export type AudienceSegmentFilter = {
  relationshipType?: AudienceRelationshipTypeValue;
  tag?: string;
  sourceModule?: AudienceSourceModuleValue;
  lastActivityDays?: number;
};

export function normalizeAudienceEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function matchesAudienceSegmentFilter(
  contact: {
    relationships: Array<{ type: string; sourceModule: string }>;
    tags: string[];
    lastActivityAt?: string | null;
  },
  filter: AudienceSegmentFilter,
): boolean {
  if (filter.relationshipType) {
    const hasRel = contact.relationships.some((rel) => rel.type === filter.relationshipType);
    if (!hasRel) return false;
  }
  if (filter.sourceModule) {
    const hasSource = contact.relationships.some((rel) => rel.sourceModule === filter.sourceModule);
    if (!hasSource) return false;
  }
  if (filter.tag) {
    if (!contact.tags.some((tag) => tag.toLowerCase() === filter.tag!.toLowerCase())) return false;
  }
  if (filter.lastActivityDays != null && contact.lastActivityAt) {
    const cutoff = Date.now() - filter.lastActivityDays * 24 * 3600_000;
    if (new Date(contact.lastActivityAt).getTime() < cutoff) return false;
  }
  return true;
}
